import { DateTime } from "luxon";
import { ChamberMemberProInvoicePaymentTermsEnum } from "../../../graphql/types";
import { normalizeCmpTimeZone } from "../../../utils/cmpTimeZones";

const invoicePaymentTermDays: Record<
  ChamberMemberProInvoicePaymentTermsEnum,
  number
> = {
  [ChamberMemberProInvoicePaymentTermsEnum.DueUponReceipt]: 0,
  [ChamberMemberProInvoicePaymentTermsEnum.Net_30]: 30,
  [ChamberMemberProInvoicePaymentTermsEnum.Net_45]: 45,
};

export const getChamberLocalDateInputValue = ({
  nowUtcMilli = Date.now(),
  timeZone,
}: {
  nowUtcMilli?: number;
  timeZone?: string | null;
}): string => {
  const normalizedTimeZone = normalizeCmpTimeZone(timeZone).timeZone;
  const chamberDate = DateTime.fromMillis(nowUtcMilli, {
    zone: normalizedTimeZone,
  });

  return chamberDate.isValid ? chamberDate.toISODate() || "" : "";
};

export const chamberDateInputToUtcMilli = ({
  dateInput,
  timeZone,
}: {
  dateInput: string;
  timeZone?: string | null;
}): number | undefined => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return undefined;
  }

  const normalizedTimeZone = normalizeCmpTimeZone(timeZone).timeZone;
  const chamberDate = DateTime.fromISO(dateInput, {
    zone: normalizedTimeZone,
  }).startOf("day");

  if (!chamberDate.isValid || chamberDate.toISODate() !== dateInput) {
    return undefined;
  }

  return chamberDate.toMillis();
};

export const resolveInvoiceDueAtUtcMilli = ({
  dueAtUtcMilli,
  issuedAtUtcMilli,
  legacyPaymentTermDays = 30,
  timeZone,
}: {
  dueAtUtcMilli?: number | null;
  issuedAtUtcMilli?: number | null;
  legacyPaymentTermDays?: number;
  timeZone?: string | null;
}): number | undefined => {
  if (dueAtUtcMilli != null && Number.isFinite(dueAtUtcMilli)) {
    return dueAtUtcMilli;
  }

  if (issuedAtUtcMilli == null || !Number.isFinite(issuedAtUtcMilli)) {
    return undefined;
  }

  const normalizedTimeZone = normalizeCmpTimeZone(timeZone).timeZone;
  const issueDate = DateTime.fromMillis(issuedAtUtcMilli, {
    zone: normalizedTimeZone,
  }).startOf("day");

  if (!issueDate.isValid) {
    return undefined;
  }

  return issueDate.plus({ days: legacyPaymentTermDays }).toMillis();
};

export const getInvoiceDueDateInput = ({
  invoicePaymentTerms,
  issueDate,
  timeZone,
}: {
  invoicePaymentTerms: ChamberMemberProInvoicePaymentTermsEnum;
  issueDate: string;
  timeZone?: string | null;
}): string => {
  const normalizedTimeZone = normalizeCmpTimeZone(timeZone).timeZone;
  const issueCalendarDate = DateTime.fromISO(issueDate, {
    zone: normalizedTimeZone,
  });

  if (!issueCalendarDate.isValid) {
    return issueDate;
  }

  return (
    issueCalendarDate
      .plus({ days: invoicePaymentTermDays[invoicePaymentTerms] })
      .toISODate() || issueDate
  );
};

export const isInvoicePastDueOnChamberDate = ({
  dueAtUtcMilli,
  nowUtcMilli = Date.now(),
  timeZone,
}: {
  dueAtUtcMilli?: number | null;
  nowUtcMilli?: number;
  timeZone?: string | null;
}): boolean => {
  if (!dueAtUtcMilli) {
    return false;
  }

  const normalizedTimeZone = normalizeCmpTimeZone(timeZone).timeZone;
  const dueDate = DateTime.fromMillis(dueAtUtcMilli, {
    zone: normalizedTimeZone,
  });
  const currentDate = DateTime.fromMillis(nowUtcMilli, {
    zone: normalizedTimeZone,
  });

  if (!dueDate.isValid || !currentDate.isValid) {
    return false;
  }

  return (
    currentDate.startOf("day").toMillis() > dueDate.startOf("day").toMillis()
  );
};
